(() => {
  const LOADED_FLAG = "__pakoBuyNowOverrideLoaded";
  const BUY_NOW_SELECTOR = "[data-pako-buy-now]";
  const CART_ADD_FORM_SELECTOR = 'form[action*="/cart/add"], form[data-js-product-form]';
  const ERROR_WRAPPER_SELECTOR = ".js-product-quantity-error-wrapper";
  const ERROR_TEXT_SELECTOR = ".js-product-quantity-error";
  const PRODUCT_JSON_SELECTOR = "[data-js-product-json]";
  const SELECTED_VARIANT_SELECTOR = "[data-selected-variant]";
  const VARIANT_INPUT_SELECTOR = '[name="id"]';
  const LOADING_CLASS = "is-loading";

  if (window[LOADED_FLAG]) {
    return;
  }

  window[LOADED_FLAG] = true;

  const busyButtons = new WeakSet();

  document.addEventListener("click", onBuyNowClick, true);
  document.addEventListener("submit", onBuyNowSubmit, true);
  document.addEventListener("change", onProductFormChange, true);
  document.addEventListener("DOMContentLoaded", syncAllBuyNowButtons);
  document.addEventListener("shopify:section:load", syncAllBuyNowButtons);

  function onBuyNowClick(event) {
    const button = event.target?.closest?.(BUY_NOW_SELECTOR);

    if (!button) {
      return;
    }

    handleBuyNow(event, button);
  }

  function onBuyNowSubmit(event) {
    const submitter = event.submitter;

    if (!submitter?.matches?.(BUY_NOW_SELECTOR)) {
      return;
    }

    handleBuyNow(event, submitter);
  }

  async function handleBuyNow(event, button) {
    stopEvent(event);

    const form = button.closest(CART_ADD_FORM_SELECTOR);

    if (!form || busyButtons.has(button) || button.disabled || button.classList.contains("disabled")) {
      return;
    }

    clearFormError(form);

    if (!isFormValid(form)) {
      return;
    }

    if (!isSelectedVariantAvailable(form)) {
      showFormError(form, "Цей варіант зараз недоступний.");
      return;
    }

    busyButtons.add(button);
    setButtonLoading(button, true);

    try {
      await clearCart();
      await addSelectedVariant(form);

      const cart = await getCart();

      window.location.assign(getCheckoutUrl(cart?.token));
    } catch (error) {
      const normalizedError = await normalizeCartError(error);

      busyButtons.delete(button);
      setButtonLoading(button, false);
      showFormError(form, getErrorMessage(normalizedError));
    }
  }

  function stopEvent(event) {
    event.preventDefault();
    event.stopPropagation();

    if (typeof event.stopImmediatePropagation === "function") {
      event.stopImmediatePropagation();
    }
  }

  function isFormValid(form) {
    if (typeof form.reportValidity === "function") {
      return form.reportValidity();
    }

    if (typeof form.checkValidity === "function") {
      return form.checkValidity();
    }

    return true;
  }

  function onProductFormChange(event) {
    const form = event.target?.closest?.(CART_ADD_FORM_SELECTOR);

    if (form) {
      syncBuyNowButton(form);
    }
  }

  function syncAllBuyNowButtons() {
    document.querySelectorAll(CART_ADD_FORM_SELECTOR).forEach(syncBuyNowButton);
  }

  function syncBuyNowButton(form) {
    const button = form.querySelector(BUY_NOW_SELECTOR);

    if (!button || busyButtons.has(button)) {
      return;
    }

    const isAvailable = isSelectedVariantAvailable(form);

    button.disabled = !isAvailable;
    button.classList.toggle("disabled", !isAvailable);
  }

  function isSelectedVariantAvailable(form) {
    const variant = getSelectedVariant(form);

    return !variant || variant.available !== false;
  }

  function getSelectedVariant(form) {
    const selectedVariant = parseJSON(form.querySelector(SELECTED_VARIANT_SELECTOR)?.textContent);

    if (selectedVariant) {
      return selectedVariant;
    }

    const product = parseJSON(form.querySelector(PRODUCT_JSON_SELECTOR)?.textContent);
    const variantId = form.querySelector(VARIANT_INPUT_SELECTOR)?.value;

    if (!product || !variantId) {
      return null;
    }

    return product.variants?.find((variant) => String(variant.id) === String(variantId)) || null;
  }

  function parseJSON(value) {
    if (!value || !value.trim()) {
      return null;
    }

    try {
      return JSON.parse(value);
    } catch (_error) {
      return null;
    }
  }

  function getRootUrl() {
    const rootUrl = window.Shopify?.routes?.root || window.themeCore?.objects?.routes?.root_url || "/";
    const normalizedRootUrl = String(rootUrl || "/").trim();

    if (!normalizedRootUrl || normalizedRootUrl === "/") {
      return "/";
    }

    return `/${normalizedRootUrl.replace(/^\/+|\/+$/g, "")}/`;
  }

  function getLocaleAwarePath(path) {
    return `${getRootUrl()}${String(path).replace(/^\/+/, "")}`;
  }

  function getCustomCheckoutUrl() {
    return getLocaleAwarePath("cart?view=pl-checkout");
  }

  function setButtonLoading(button, isLoading) {
    button.classList.toggle(LOADING_CLASS, isLoading);
    button.toggleAttribute("aria-busy", isLoading);
    button.disabled = isLoading;
  }

  async function clearCart() {
    const response = await fetch(getLocaleAwarePath("cart/clear.js"), {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json"
      }
    });

    if (!response.ok) {
      throw response;
    }

    return response.json().catch(() => null);
  }

  async function addSelectedVariant(form) {
    const formData = new FormData(form);

    if (!formData.has("quantity")) {
      formData.set("quantity", "1");
    }

    if (!formData.has("return_to")) {
      formData.set("return_to", getCustomCheckoutUrl());
    }

    const response = await fetch(getLocaleAwarePath("cart/add.js"), {
      method: "POST",
      credentials: "same-origin",
      headers: {
        Accept: "application/json"
      },
      body: formData
    });

    if (!response.ok) {
      throw response;
    }

    return response.json().catch(() => null);
  }

  async function getCart() {
    const response = await fetch(getLocaleAwarePath("cart.js"), {
      method: "GET",
      credentials: "same-origin",
      headers: {
        Accept: "application/json"
      }
    });

    if (!response.ok) {
      return null;
    }

    return response.json().catch(() => null);
  }

  function getCheckoutUrl(cartToken) {
    return getCustomCheckoutUrl();
  }

  async function normalizeCartError(error) {
    if (!error || typeof error.json !== "function") {
      return error;
    }

    try {
      return await error.json();
    } catch (_parseError) {
      return error;
    }
  }

  function getErrorMessage(error) {
    if (!error) {
      return "Не вдалося перейти до оформлення замовлення. Спробуйте ще раз.";
    }

    if (typeof error === "string") {
      return error;
    }

    if (error.description) {
      return error.description;
    }

    if (error.message) {
      return error.message;
    }

    if (error.errors && typeof error.errors === "object") {
      return Object.values(error.errors).flat().join(" ");
    }

    return "Не вдалося перейти до оформлення замовлення. Спробуйте ще раз.";
  }

  function clearFormError(form) {
    const errorWrapper = form.querySelector(ERROR_WRAPPER_SELECTOR);
    const errorText = form.querySelector(ERROR_TEXT_SELECTOR);

    errorWrapper?.classList.add("is-hidden");

    if (errorText) {
      errorText.textContent = "";
    }
  }

  function showFormError(form, message) {
    const errorWrapper = form.querySelector(ERROR_WRAPPER_SELECTOR)
      || form.querySelector(ERROR_TEXT_SELECTOR)?.closest(".product-add-to-cart__error");
    const errorText = form.querySelector(ERROR_TEXT_SELECTOR);

    if (errorText) {
      errorText.textContent = message;
    }

    errorWrapper?.classList.remove("is-hidden");
  }
})();
